import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { EvolutionChartConfig, EvolutionChartData, Params } from '@empathyco/insights'
import type { Ref } from 'vue'

import type { KpiEnum } from '../enums'
import type { ContextState } from '../models'

import type { KpisOptionComposable } from './use-kpis'
import { EvolutionChartComponent } from '@empathyco/insights'

import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ANALYTICS_CONSTS } from '../store/analytics/analytics.consts'

import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { mapActions, mapGetters } from './utils'

export interface KpisLegendItem {
  id: string
  label: string
}

interface BaseDistributionChartData {
  date: Date
}

export function useDistributionChart<Type extends BaseDistributionChartData>(
  kpiOptions: KpisOptionComposable[],
  kpisItems: KpisLegendItem[],
  getKpiValueFn: (kpi: KpiEnum) => (data: Type) => number,
): {
  timezone: string
  component: EvolutionChartComponent
  selectedKpis: Ref<KpiEnum[]>
  chartData: Ref<Type[]>
  isDailyData: Ref<boolean>
  context: Ref<ContextState>
  fetchStatsReport: (params: any) => Promise<StatsResponse>
  loadDistData: () => Promise<void>
} {
  const ctx = getCurrentInstance()
  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const timezone = ctx.proxy.$epb.messages.insights.analytics.timezone

  const { context } = mapGetters<ContextState>(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
    context: CONTEXT_CONSTS.getters.context,
  })

  const { fetchStatsReport, loadDistData } = mapActions(
    ctx.proxy.$store,
    ANALYTICS_CONSTS.moduleName,
    {
      fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
      loadDistData: ANALYTICS_CONSTS.actions.loadDistData,
    },
  )

  const component = new EvolutionChartComponent()

  const selectedKpis = ref<KpiEnum[]>([])

  const chartData = ref<Type[]>([]) as Ref<Type[]>

  const isDailyData = computed<boolean>(() => {
    const selectedValues = context.value.filters.date.selectedValues as any[]
    return selectedValues[1] - selectedValues[0] <= 1000 * 60 * 60 * 24
  })

  const areaChartData = computed<EvolutionChartData>(() => {
    let data: any[] = []
    if (chartData.value) {
      data = chartData.value.map(distributionDataItem => {
        return getAreaChartDataItem(distributionDataItem)
      })
    }
    return data
  })

  const areaChartParams = computed<Params<EvolutionChartConfig, EvolutionChartData>>(() => {
    return {
      config: {
        filteredValues: kpisItems.filter(item => {
          return selectedKpis.value.includes(item.id as KpiEnum)
        }),
        maxValue: undefined,
        valueUnits: undefined,
      },
      data: areaChartData.value ?? [],
    }
  })

  const getDateWithoutTimezone = (date: Date): Date => {
    return new Date(date.getTime() + date.getTimezoneOffset() * (60 * 1000))
  }

  const getAreaChartDataItemLabels = (distributionDataItem: Type): string[] => {
    const dateWithoutOffset = getDateWithoutTimezone(distributionDataItem.date)
    const labels = [
      dateWithoutOffset.toLocaleString(
        ctx.proxy.$epb.locale,
        isDailyData.value
          ? {
              hour: '2-digit',
              minute: '2-digit',
            }
          : {
              month: 'short',
              day: 'numeric',
            },
      ),
    ]
    return labels
  }

  const getInitialSelectedKpis = (): KpiEnum[] => {
    return kpisItems.map(kpiLegendItem => kpiLegendItem.id) as KpiEnum[]
  }

  const getKpiValues = (distributionData: Type, kpi: KpiEnum): [number] | [number, number] => {
    const getKpiValueFna = getKpiValueFn(kpi)
    const kpiValue = getKpiValueFna(distributionData)

    return [kpiValue]
  }

  const getAreaChartDataItemValues = (
    distributionDataItem: Type,
  ): Partial<Record<KpiEnum, [number] | [number, number]>> => {
    const values: Partial<Record<KpiEnum, [number] | [number, number]>> = {}

    kpiOptions.forEach(({ id }: KpisOptionComposable) => {
      values[id] = getKpiValues(distributionDataItem, id)
    })

    return values
  }

  function getAreaChartDataItem(distributionDataItem: Type): any {
    return {
      id: distributionDataItem.date.toISOString(),
      labels: getAreaChartDataItemLabels(distributionDataItem),
      values: getAreaChartDataItemValues(distributionDataItem),
    }
  }

  watch(
    () => areaChartParams.value,
    () => {
      component.update(areaChartParams.value)
    },
    { deep: true },
  )

  onMounted(() => {
    selectedKpis.value = getInitialSelectedKpis()
    component.init(ctx.proxy.$refs.component as HTMLElement, areaChartParams.value)
  })

  onBeforeUnmount(() => {
    component.destroy()
  })

  return {
    timezone,
    component,
    selectedKpis,
    chartData,
    isDailyData,
    context,
    fetchStatsReport,
    loadDistData,
  }
}
